/**
 * Colours and fonts of the preview's editor overlay, resolved from the design tokens (src/ui/styles/tokens.css).
 * Canvas drawing cannot use `var(--token)`, so the stage reads the computed custom properties of its root element
 * when it mounts and when the theme changes. Each entry lists tokens most specific first; without any stylesheet
 * (tests, detached elements) the last resort is a CSS system colour keyword, never a literal colour.
 */

export interface OverlayTheme {
  readonly accent: string;
  readonly accentMuted: string;
  readonly selection: string;
  readonly text: string;
  readonly textMuted: string;
  /** Label background. */
  readonly surface: string;
  readonly border: string;
  /** Dashed outlines of lines Phira draws nothing for. */
  readonly invisible: string;
  /** Hidden-note ghost outlines. */
  readonly ghost: string;
  readonly hover: string;
  readonly danger: string;
  readonly warning: string;
  readonly success: string;
  readonly info: string;
  readonly guide: string;
  readonly fontSans: string;
}

type Reader = (name: string) => string;

const TOKENS: Readonly<Record<keyof OverlayTheme, readonly string[]>> = {
  accent: ['--color-preview-accent', '--color-accent', 'Highlight'],
  accentMuted: ['--color-accent-muted', '--color-selection', 'Highlight'],
  selection: ['--color-preview-selection', '--color-accent', 'Highlight'],
  text: ['--color-text', 'CanvasText'],
  textMuted: ['--color-text-muted', 'GrayText'],
  surface: ['--color-surface-overlay', '--color-surface-2', 'Canvas'],
  border: ['--color-border-strong', '--color-border', 'GrayText'],
  invisible: ['--color-preview-invisible', '--color-badge-in-use', '--color-text-muted', 'GrayText'],
  ghost: ['--color-preview-ghost', '--color-warning', 'Mark'],
  hover: ['--color-preview-hover', '--color-info', 'Highlight'],
  danger: ['--color-danger', 'Mark'],
  warning: ['--color-warning', 'Mark'],
  success: ['--color-success', 'Highlight'],
  info: ['--color-info', 'Highlight'],
  guide: ['--color-preview-guide', '--color-badge-rpe17', '--color-info', 'Highlight'],
  fontSans: ['--font-sans', 'sans-serif'],
};

function resolve(read: Reader, candidates: readonly string[]): string {
  for (const c of candidates) {
    if (!c.startsWith('--')) return c;
    const v = read(c).trim();
    if (v !== '') return v;
  }
  return '';
}

export function themeFromTokens(read: Reader): OverlayTheme {
  const out = {} as Record<keyof OverlayTheme, string>;
  for (const k of Object.keys(TOKENS) as (keyof OverlayTheme)[]) out[k] = resolve(read, TOKENS[k]);
  return Object.freeze(out);
}

/** The overlay theme visible at an element (its computed style), or the system-colour fallbacks. */
export function readOverlayTheme(el: Element | null): OverlayTheme {
  if (el === null || typeof getComputedStyle !== 'function') return themeFromTokens(() => '');
  let style: CSSStyleDeclaration | null = null;
  try {
    style = getComputedStyle(el);
  } catch {
    style = null;
  }
  return themeFromTokens((name) => style?.getPropertyValue(name) ?? '');
}

let current: OverlayTheme = themeFromTokens(() => '');

/** The theme the overlay draws with (set by the stage). */
export function overlayTheme(): OverlayTheme {
  return current;
}

export function setOverlayTheme(t: OverlayTheme): void {
  current = t;
}
